import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidateService } from '../services/candidateService';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';



const CandidateProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    mobile: '',
    dateOfBirth: '',
    gender: '',
    state: '',
    district: '',
    passportNumber: '',
    passportExpiry: '',
    nationality: '',
    address: '',
    emergencyContact: '',
    jobRole: '',
    experience: '',
    education: ''
  });

  useEffect(() => {
    if (user) {
      fetchCandidateProfile();
    }
  }, [user]);

  const fetchCandidateProfile = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!user) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      // For non-candidate users, use the user data directly
      if (user.role !== 'CANDIDATE') {
        setCandidate({
          personalInformation: {
            name: user.name || '',
            mobile: user.phoneNumber || '',
            profilePhoto: { url: null }
          },
          userId: {
            email: user.email || '',
            phoneNumber: user.phoneNumber || ''
          },
          professionalDetails: {
            currentJobRole: user.role || '',
            workExperience: '',
            education: ''
          }
        });
        
        setFormData({
          name: user.name || '',
          email: user.email || '',
          mobile: user.phoneNumber || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : '',
          gender: user.gender || '',
          state: user.state || '',
          district: user.district || '',
          passportNumber: '',
          passportExpiry: '',
          nationality: user.nationality || '',
          address: user.address || '',
          emergencyContact: '',
          jobRole: user.role || '',
          experience: '',
          education: ''
        });
        
        setLoading(false);
        return;
      }

      // For candidates, fetch candidate profile as before
      const userId = user._id || user.id;
      if (!userId) {
        setError('User ID not found');
        setLoading(false);
        return;
      }

      const response = await candidateService.getCandidateByUserId(userId);
      setCandidate(response.data);
      
      // Handle both new and legacy data structures
      const candidateData = response.data;
      setFormData({
        // New structure fields
        name: candidateData.personalInformation?.name || '',
        email: candidateData.userId?.email || '',
        mobile: candidateData.personalInformation?.mobile || candidateData.userId?.phoneNumber || '',
        state: candidateData.personalInformation?.location?.state || '',
        district: candidateData.personalInformation?.location?.district || '',
        // Legacy structure fields
        firstName: candidateData.firstName || '',
        lastName: candidateData.lastName || '',
        dateOfBirth: candidateData.dateOfBirth ? candidateData.dateOfBirth.split('T')[0] : '',
        gender: candidateData.personalInformation?.gender || candidateData.gender || '',
        passportNumber: candidateData.personalInformation?.passportNumber || candidateData.passportNumber || '',
        passportExpiry: candidateData.passportExpiry ? candidateData.passportExpiry.split('T')[0] : '',
        nationality: candidateData.nationality || '',
        address: candidateData.address || '',
        emergencyContact: candidateData.emergencyContact || '',
        jobRole: candidateData.professionalDetails?.currentJobRole || candidateData.jobRole || '',
        experience: candidateData.professionalDetails?.workExperience || candidateData.experience || '',
        education: candidateData.professionalDetails?.education || candidateData.education || ''
      });
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load profile';
      setError(errorMsg);
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // For non-candidate users, we don't update candidate profile
      if (user.role !== 'CANDIDATE') {
        // For now, just show success message since we don't have user update API
        setSuccess('Profile information displayed successfully');
        setIsEditing(false);
        return;
      }

      // Transform formData to match the Candidate model structure
      const updatePayload = {
        // New nested structure
        personalInformation: {
          name: formData.name,
          mobile: formData.mobile,
          gender: formData.gender,
          passportNumber: formData.passportNumber,
          location: {
            state: formData.state,
            district: formData.district
          }
        },
        professionalDetails: {
          currentJobRole: formData.jobRole,
          workExperience: formData.experience,
          education: formData.education
        },
        // Legacy fields for backward compatibility
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        passportExpiry: formData.passportExpiry,
        nationality: formData.nationality,
        address: formData.address,
        emergencyContact: formData.emergencyContact || undefined,
        // Update User's email and phone
        email: formData.email,
        mobile: formData.mobile
      };

      // For candidates, update candidate profile
      await candidateService.updateCandidate(candidate._id, updatePayload);
      setSuccess('Profile updated successfully');
      setIsEditing(false);
      fetchCandidateProfile();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError('');
    setSuccess('');
    
    // Reset form data to original data
    if (user.role !== 'CANDIDATE') {
      // For non-candidate users, reset to user data
      setFormData({
        name: user.name || '',
        email: user.email || '',
        mobile: user.phoneNumber || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : '',
        gender: user.gender || '',
        state: user.state || '',
        district: user.district || '',
        passportNumber: '',
        passportExpiry: '',
        nationality: user.nationality || '',
        address: user.address || '',
        emergencyContact: '',
        jobRole: user.role || '',
        experience: '',
        education: ''
      });
    } else if (candidate) {
      // For candidates, reset to candidate data
      setFormData({
        // New structure fields
        name: candidate.personalInformation?.name || '',
        email: candidate.userId?.email || '',
        mobile: candidate.personalInformation?.mobile || candidate.userId?.phoneNumber || '',
        state: candidate.personalInformation?.location?.state || '',
        district: candidate.personalInformation?.location?.district || '',
        // Legacy structure fields
        firstName: candidate.firstName || '',
        lastName: candidate.lastName || '',
        dateOfBirth: candidate.dateOfBirth ? candidate.dateOfBirth.split('T')[0] : '',
        gender: candidate.personalInformation?.gender || candidate.gender || '',
        passportNumber: candidate.personalInformation?.passportNumber || candidate.passportNumber || '',
        passportExpiry: candidate.passportExpiry ? candidate.passportExpiry.split('T')[0] : '',
        nationality: candidate.nationality || '',
        address: candidate.address || '',
        emergencyContact: candidate.emergencyContact || '',
        jobRole: candidate.professionalDetails?.currentJobRole || candidate.jobRole || '',
        experience: candidate.professionalDetails?.workExperience || candidate.experience || '',
        education: candidate.professionalDetails?.education || candidate.education || ''
      });
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setLoading(true);

      const response = await candidateService.uploadProfilePhoto(file);
      
      if (response.success) {
        setSuccess('Profile photo updated successfully!');
        // Refresh the profile to show new photo
        await fetchCandidateProfile();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload profile photo');
    } finally {
      setLoading(false);
      // Clear the file input
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="text-center p-12 text-primary text-[1.1rem]">Loading profile...</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex justify-between items-center mb-8 lg:flex-col lg:items-start lg:gap-4">
        <h1 className="text-3xl font-bold text-primary m-0 lg:text-2xl">My Profile</h1>
        {!isEditing && (
          <button className="bg-accent-green text-white border-none py-3 px-8 rounded-lg cursor-pointer text-base font-semibold transition-all duration-300 hover:bg-[#5a6e4d] hover:shadow-lg hover:-translate-y-0.5" onClick={() => setIsEditing(true)}>
            ✎ Edit Profile
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 border-l-4 border-red-500 flex items-start gap-3">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6 border-l-4 border-green-500 flex items-start gap-3">{success}</div>}

      <div className="bg-white rounded-xl p-8 shadow-md lg:p-6">
        {!isEditing ? (
          <div className="profile-view">{/* Profile Image Section */}
            <div className="flex flex-col items-center mb-12 pb-8 border-b-2 border-background">
              <div className="relative">
                <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-accent-green shadow-lg bg-gradient-to-br from-accent-green/10 to-accent-green/5">
                  {(() => {
                    const photoUrl = candidate?.personalInformation?.profilePhoto?.url || 
                                    candidate?.profilePhoto?.url || 
                                    candidate?.photoUrl;
                    
                    return photoUrl ? (
                      <img 
                        src={photoUrl} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = e.target.parentNode.querySelector('.placeholder-avatar');
                          if (placeholder) placeholder.style.display = 'flex';
                        }}
                      />
                    ) : null;
                  })()}
                  <div className="placeholder-avatar w-full h-full bg-gradient-to-br from-accent-green/20 to-accent-green/30 flex items-center justify-center" 
                       style={{display: (() => {
                         const photoUrl = candidate?.personalInformation?.profilePhoto?.url || 
                                         candidate?.profilePhoto?.url || 
                                         candidate?.photoUrl;
                         return photoUrl ? 'none' : 'flex';
                       })()}}>
                    <svg className="w-20 h-20 text-accent-green/60" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <button
                  onClick={() => document.getElementById('profile-photo-input').click()}
                  className="absolute bottom-2 right-2 w-10 h-10 bg-accent-green rounded-full border-2 border-white flex items-center justify-center hover:bg-accent-green-dark transition-colors cursor-pointer shadow-lg"
                  title="Change profile photo"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                <input
                  id="profile-photo-input"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
              <h2 className="text-3xl font-bold text-primary text-center mt-6">
                {candidate?.personalInformation?.name || `${candidate?.firstName || ''} ${candidate?.lastName || ''}`.trim() || 'Candidate Name'}
              </h2>
              <p className="text-base text-text-secondary mt-2">
                {candidate?.professionalDetails?.currentJobRole || candidate?.jobRole || 'Professional'}
              </p>
              <div className="flex gap-2 mt-4">
                <span className="px-4 py-1.5 bg-accent-green/10 text-accent-green rounded-full text-sm font-medium">Active Profile</span>
                <span className="px-4 py-1.5 bg-background text-primary rounded-full text-sm font-medium">Member since {candidate?.createdAt ? new Date(candidate.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</span>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="mb-10 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent-green/10 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-primary mb-0">Personal Information</h2>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-6 lg:grid-cols-1">
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" /></svg>
                    Full Name
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">
                    {candidate?.personalInformation?.name || `${candidate?.firstName || ''} ${candidate?.lastName || ''}`.trim() || 'Not provided'}
                  </p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" /><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" /></svg>
                    Email
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium break-all">{candidate?.userId?.email || 'Not provided'}</p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.797l.291 1.455a1 1 0 01-.926 1.165H6.75a11.001 11.001 0 0110.974 10.974h1.457a1 1 0 011.163-.926l1.455.291a1 1 0 01.796.986V17a1 1 0 01-1 1H17c-6.075 0-11-4.925-11-11V3z" /></svg>
                    Mobile
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">
                    {candidate?.personalInformation?.mobile || candidate?.userId?.phoneNumber || 'Not provided'}
                  </p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v2H4a2 2 0 00-2 2v2h16V7a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v2H7V3a1 1 0 00-1-1zm0 5a2 2 0 002 2h8a2 2 0 002-2H6z" clipRule="evenodd" /></svg>
                    Date of Birth
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">{candidate?.dateOfBirth ? new Date(candidate.dateOfBirth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not provided'}</p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V3.5A1.5 1.5 0 0015.5 2h-11zM4 3.5a.5.5 0 01.5-.5h11a.5.5 0 01.5.5V4H4v-.5zm6.5 11a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
                    Gender
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">
                    {candidate?.personalInformation?.gender || candidate?.gender || 'Not provided'}
                  </p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" /></svg>
                    State
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">
                    {candidate?.personalInformation?.location?.state || 'Not provided'}
                  </p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" /></svg>
                    District
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">
                    {candidate?.personalInformation?.location?.district || 'Not provided'}
                  </p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" /></svg>
                    Nationality
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">{candidate?.nationality || 'Not provided'}</p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" /></svg>
                    Emergency Contact
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">{candidate?.emergencyContact || 'Not provided'}</p>
                </div>
              </div>
            </div>

            {/* Passport Information Section */}
            <div className="mb-10 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent-green/10 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v12m8-12v12m4-12H4" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-primary mb-0">Passport Information</h2>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-6 lg:grid-cols-1">
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" /></svg>
                    Passport Number
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium font-mono">
                    {candidate?.personalInformation?.passportNumber || candidate?.passportNumber || 'Not provided'}
                  </p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v2H4a2 2 0 00-2 2v2h16V7a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v2H7V3a1 1 0 00-1-1zm0 5a2 2 0 002 2h8a2 2 0 002-2H6z" clipRule="evenodd" /></svg>
                    Expiry Date
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">{candidate?.passportExpiry ? new Date(candidate.passportExpiry).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not provided'}</p>
                </div>
              </div>
            </div>

            {/* Professional Information Section */}
            <div className="mb-10 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent-green/10 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.576-9-1.636M16 6h4m-1 0a3 3 0 11-6 0 3 3 0 016 0zM16 13h4m-8 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-primary mb-0">Professional Information</h2>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-6 lg:grid-cols-1">
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>
                    Job Role
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">
                    {candidate?.professionalDetails?.currentJobRole || candidate?.jobRole || 'Not provided'}
                  </p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" /></svg>
                    Experience
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">
                    {candidate?.professionalDetails?.workExperience || candidate?.experience || 'Not provided'}
                  </p>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors col-span-full">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10.5 1.5H19a1 1 0 011 1v1a1 1 0 01-1 1h-.5v13a1 1 0 01-1 1h-3a1 1 0 01-1-1v-2H8v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4.5H2.5a1 1 0 01-1-1v-1a1 1 0 011-1h8.5V1.5z" /></svg>
                    Education
                  </label>
                  <p className="text-primary text-base m-0 py-2 font-medium">
                    {candidate?.professionalDetails?.education || candidate?.education || 'Not provided'}
                  </p>
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="mb-10 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent-green/10 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-primary mb-0">Address</h2>
              </div>
              <div className="p-4 rounded-lg bg-background/50 border border-background">
                <p className="text-primary text-base m-0 py-3 whitespace-pre-wrap leading-relaxed">{candidate?.address || 'Not provided'}</p>
              </div>
            </div>

            {/* Documents Section */}
            <div className="mb-10 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent-green/10 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-primary mb-0">Documents</h2>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-6 lg:grid-cols-1">
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v4h8v-4zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" /></svg>
                    Resume
                  </label>
                  <div className="text-primary text-base m-0 py-2">
                    {(() => {
                      const resumeUrl = candidate?.documents?.resume?.url || candidate?.resumeUrl;
                      return resumeUrl ? (
                        <a 
                          href={resumeUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-green text-white rounded-lg hover:bg-[#5a6e4d] transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          View Resume
                        </a>
                      ) : (
                        <span className="inline-block px-4 py-2 bg-gray-200 text-gray-600 rounded-lg">Not uploaded</span>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex flex-col p-4 rounded-lg bg-background/50 border border-background hover:border-accent-green/30 transition-colors">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" /></svg>
                    Combined Documents
                  </label>
                  <div className="text-primary text-base m-0 py-2">
                    {(() => {
                      const combinedDocsUrl = candidate?.documents?.combinedDocuments?.url;
                      return combinedDocsUrl ? (
                        <a 
                          href={combinedDocsUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-green text-white rounded-lg hover:bg-[#5a6e4d] transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          View Documents
                        </a>
                      ) : (
                        <span className="inline-block px-4 py-2 bg-gray-200 text-gray-600 rounded-lg">Not uploaded</span>
                      );
                    })()}
                  </div>
                </div>
                {candidate?.additionalDocuments && candidate.additionalDocuments.length > 0 && (
                  <div className="flex flex-col col-span-full p-4 rounded-lg bg-background/50 border border-background">
                    <label className="font-semibold text-accent-green mb-4 text-sm uppercase tracking-wide flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>
                      Additional Documents
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {candidate.additionalDocuments.map((doc, index) => (
                        <a 
                          key={index}
                          href={doc.documentUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-green/10 text-accent-green rounded-lg hover:bg-accent-green/20 transition-colors font-medium"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          {doc.documentName || `Document ${index + 1}`}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="w-full">
            <div className="mb-10 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent-green/10 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-primary mb-0">Personal Information</h2>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-6 lg:grid-cols-1">
                {/* Show appropriate name fields based on data structure */}
                {candidate?.personalInformation?.name ? (
                  <div className="flex flex-col col-span-full">
                    <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col">
                      <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                      />
                    </div>
                  </>
                )}
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Mobile Number</label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Date of Birth</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                    min={new Date(new Date().setFullYear(new Date().getFullYear() - 65)).toISOString().split('T')[0]}
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-2 text-[0.9rem]">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="Enter your state"
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">District</label>
                  <input
                    type="text"
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    placeholder="Enter your district"
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Nationality</label>
                  <input
                    type="text"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Emergency Contact</label>
                  <input
                    type="tel"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleChange}
                    placeholder="+1234567890"
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
              </div>
            </div>

            <div className="mb-10 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent-green/10 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v12m8-12v12m4-12H4" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-primary mb-0">Passport Information</h2>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-6 lg:grid-cols-1">
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Passport Number *</label>
                  <input
                    type="text"
                    name="passportNumber"
                    value={formData.passportNumber}
                    onChange={handleChange}
                    required
                    disabled
                    title="Passport number cannot be changed"
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)] disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-600"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Passport Expiry</label>
                  <input
                    type="date"
                    name="passportExpiry"
                    value={formData.passportExpiry}
                    onChange={handleChange}
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
              </div>
            </div>

            <div className="mb-10 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent-green/10 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.576-9-1.636M16 6h4m-1 0a3 3 0 11-6 0 3 3 0 016 0zM16 13h4m-8 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-primary mb-0">Professional Information</h2>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-6 lg:grid-cols-1">
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Job Role</label>
                  <input
                    type="text"
                    name="jobRole"
                    value={formData.jobRole}
                    onChange={handleChange}
                    placeholder="e.g., Nurse, Doctor"
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Experience</label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="e.g., 5 years"
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
                <div className="flex flex-col col-span-full">
                  <label className="font-semibold text-accent-green mb-3 text-sm uppercase tracking-wide">Education</label>
                  <input
                    type="text"
                    name="education"
                    value={formData.education}
                    onChange={handleChange}
                    placeholder="e.g., Bachelor's in Nursing"
                    className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                  />
                </div>
              </div>
            </div>

            <div className="mb-10 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent-green/10 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-primary mb-0">Address</h2>
              </div>
              <div className="flex flex-col">
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Enter your full address"
                  className="p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 font-[inherit] resize-vertical min-h-[120px] focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-8 pt-8 border-t-2 border-background lg:flex-col">
              <button type="button" className="py-3 px-8 border-2 border-gray-300 rounded-lg text-base cursor-pointer transition-all duration-300 font-semibold bg-white text-primary hover:bg-gray-50 hover:border-gray-400 lg:w-full flex items-center justify-center gap-2" onClick={handleCancel}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                Cancel
              </button>
              <button type="submit" className="py-3 px-8 border-none rounded-lg text-base cursor-pointer transition-all duration-300 font-semibold bg-accent-green text-white hover:bg-[#5a6e4d] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(107,127,92,0.3)] lg:w-full flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Save Changes
              </button>
            </div>
          </form>
        )}
      </div>
    </MainLayout>
  );
};

export default CandidateProfilePage;
